import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";

const MaterialsShowcase = () => {
  const [selectedMaterial, setSelectedMaterial] = useState(0);

  const materials = [
    {
      id: 1,
      name: "Mogno Brasileiro",
      scientificName: "Swietenia macrophylla",
      category: "Madeira Nobre",
      origin: "Amazônia Brasileira",
      characteristics: [
        "Densidade: 650 kg/m³",
        "Durabilidade: Muito Alta",
        "Resistência: Excelente",
        "Trabalhabilidade: Ótima",
      ],
      description: `O mogno brasileiro é considerado uma das madeiras mais nobres do mundo. Sua coloração avermelhada única e veios elegantes fazem dele a escolha perfeita para móveis de luxo.\n\nSua estabilidade dimensional excepcional e resistência natural a pragas garantem durabilidade por gerações, tornando cada peça um verdadeiro investimento.`,
      image:
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop",
      texture:
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      applications: [
        "Mesas de jantar",
        "Estantes",
        "Móveis executivos",
        "Painéis decorativos",
      ],
      price: "Premium",
    },
    {
      id: 2,
      name: "Ipê Amarelo",
      scientificName: "Handroanthus albus",
      category: "Madeira de Lei",
      origin: "Cerrado Brasileiro",
      characteristics: [
        "Densidade: 950 kg/m³",
        "Durabilidade: Extrema",
        "Resistência: Superior",
        "Trabalhabilidade: Desafiadora",
      ],
      description: `O ipê amarelo é uma das madeiras mais resistentes do planeta. Sua densidade excepcional e resistência natural fazem dele ideal para móveis que precisam suportar uso intenso.\n\nSua coloração dourada e veios marcantes criam peças de beleza incomparável, perfeitas para ambientes que exigem sofisticação e durabilidade.`,
      image:
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop",
      texture:
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      applications: [
        "Mesas de centro",
        "Cadeiras",
        "Estruturas",
        "Detalhes decorativos",
      ],
      price: "Ultra Premium",
    },
    {
      id: 3,
      name: "Jatobá",
      scientificName: "Hymenaea courbaril",
      category: "Madeira Nacional",
      origin: "Mata Atlântica",
      characteristics: [
        "Densidade: 850 kg/m³",
        "Durabilidade: Alta",
        "Resistência: Muito Boa",
        "Trabalhabilidade: Boa",
      ],
      description: `O jatobá é uma madeira brasileira de excepcional beleza e resistência. Sua coloração que varia do castanho claro ao avermelhado oferece versatilidade única para diferentes estilos.\n\nCom excelente estabilidade e facilidade de trabalho, é perfeita para móveis que combinam funcionalidade e elegância natural.`,
      image:
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop",
      texture:
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      applications: [
        "Cômodas",
        "Criados-mudos",
        "Prateleiras",
        "Móveis infantis",
      ],
      price: "Acessível Premium",
    },
    {
      id: 4,
      name: "Cerejeira Brasileira",
      scientificName: "Amburana cearensis",
      category: "Madeira Aromática",
      origin: "Caatinga e Cerrado",
      characteristics: [
        "Densidade: 550 kg/m³",
        "Durabilidade: Boa",
        "Resistência: Boa",
        "Trabalhabilidade: Excelente",
      ],
      description: `A cerejeira brasileira é famosa por seu aroma natural único e coloração clara elegante. Sua textura suave e facilidade de trabalho permitem acabamentos excepcionais.\n\nIdeal para móveis que precisam de detalhes refinados e para ambientes onde o aroma natural da madeira é desejado.`,
      image:
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop",
      texture:
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop",
      applications: [
        "Guarda-roupas",
        "Móveis de quarto",
        "Detalhes entalhados",
        "Móveis aromáticos",
      ],
      price: "Médio Premium",
    },
  ];

  const currentMaterial = materials?.[selectedMaterial];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
              <Icon name="TreePine" size={24} color="white" strokeWidth={2.5} />
            </div>
            <span className="font-inter text-secondary font-semibold text-lg">
              Materiais Premium
            </span>
          </div>

          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Madeiras Nobres
            <span className="block text-accent">Selecionadas</span>
          </h2>

          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Trabalhamos exclusivamente com madeiras brasileiras de origem
            certificada, cada uma escolhida por suas características únicas e
            sustentabilidade.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Material Selector */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-2xl p-6 shadow-lg border border-border sticky top-24">
              <h3 className="font-playfair text-xl font-bold text-primary mb-6">
                Selecione uma Madeira
              </h3>

              <div className="space-y-3">
                {materials?.map((material, index) => (
                  <button
                    key={material?.id}
                    onClick={() => setSelectedMaterial(index)}
                    className={`w-full text-left p-4 rounded-xl transition-all duration-300 ${
                      selectedMaterial === index
                        ? "bg-primary text-primary-foreground shadow-lg"
                        : "bg-muted hover:bg-muted/80 text-text-primary"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-12 h-12 rounded-lg overflow-hidden ${
                          selectedMaterial === index ? "ring-2 ring-white" : ""
                        }`}
                      >
                        <Image
                          src={material?.texture}
                          alt={`Textura ${material?.name}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{material?.name}</div>
                        <div
                          className={`text-sm ${
                            selectedMaterial === index
                              ? "text-primary-foreground/80"
                              : "text-text-secondary"
                          }`}
                        >
                          {material?.category}
                        </div>
                      </div>
                      <Icon
                        name="ChevronRight"
                        size={16}
                        className={
                          selectedMaterial === index
                            ? "text-primary-foreground"
                            : "text-text-secondary"
                        }
                      />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Material Details */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-2xl overflow-hidden shadow-lg border border-border">
              {/* Material Image */}
              <div className="relative h-80">
                <Image
                  src={currentMaterial?.image}
                  alt={`Madeira ${currentMaterial?.name}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

                {/* Price Badge */}
                <div className="absolute top-6 right-6 bg-accent text-accent-foreground px-4 py-2 rounded-full font-semibold">
                  {currentMaterial?.price}
                </div>
              </div>

              {/* Material Info */}
              <div className="p-8">
                <div className="mb-6">
                  <h3 className="font-playfair text-3xl font-bold text-primary mb-2">
                    {currentMaterial?.name}
                  </h3>
                  <div className="flex items-center space-x-4 text-text-secondary">
                    <span className="italic">
                      {currentMaterial?.scientificName}
                    </span>
                    <span>•</span>
                    <span>{currentMaterial?.origin}</span>
                  </div>
                </div>

                <div className="prose prose-lg max-w-none mb-8">
                  <p className="text-text-primary leading-relaxed whitespace-pre-line">
                    {currentMaterial?.description}
                  </p>
                </div>

                {/* Characteristics Grid */}
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h4 className="font-semibold text-primary mb-4 flex items-center space-x-2">
                      <Icon name="BarChart3" size={20} />
                      <span>Características Técnicas</span>
                    </h4>
                    <ul className="space-y-3">
                      {currentMaterial?.characteristics?.map((char, index) => (
                        <li key={index} className="flex items-center space-x-3">
                          <Icon
                            name="Check"
                            size={16}
                            color="var(--color-success)"
                            strokeWidth={2.5}
                          />
                          <span className="text-text-secondary">{char}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-primary mb-4 flex items-center space-x-2">
                      <Icon name="Hammer" size={20} />
                      <span>Aplicações Principais</span>
                    </h4>
                    <ul className="space-y-3">
                      {currentMaterial?.applications?.map((app, index) => (
                        <li key={index} className="flex items-center space-x-3">
                          <Icon
                            name="ArrowRight"
                            size={16}
                            color="var(--color-accent)"
                            strokeWidth={2.5}
                          />
                          <span className="text-text-secondary">{app}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sustainability Section */}
        <div className="mt-20">
          <div className="bg-gradient-to-r from-success/10 to-primary/10 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center">
                    <Icon
                      name="Leaf"
                      size={24}
                      color="white"
                      strokeWidth={2.5}
                    />
                  </div>
                  <span className="font-inter text-success font-semibold text-lg">
                    Sustentabilidade
                  </span>
                </div>

                <h3 className="font-playfair text-2xl md:text-3xl font-bold text-primary mb-4">
                  Compromisso com o Futuro
                </h3>

                <p className="text-text-secondary leading-relaxed mb-6">
                  Todas as nossas madeiras são provenientes de manejo
                  sustentável certificado. Trabalhamos apenas com fornecedores
                  que seguem práticas responsáveis de extração e
                  reflorestamento.
                </p>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Icon
                      name="Check"
                      size={16}
                      color="var(--color-success)"
                      strokeWidth={2.5}
                    />
                    <span className="text-text-secondary">
                      Certificação FSC e PEFC
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon
                      name="Check"
                      size={16}
                      color="var(--color-success)"
                      strokeWidth={2.5}
                    />
                    <span className="text-text-secondary">
                      Rastreabilidade completa
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon
                      name="Check"
                      size={16}
                      color="var(--color-success)"
                      strokeWidth={2.5}
                    />
                    <span className="text-text-secondary">
                      Apoio ao reflorestamento
                    </span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <Image
                  src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&h=400&fit=crop"
                  alt="Floresta sustentável"
                  className="w-full h-64 object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-success/20 rounded-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MaterialsShowcase;
